import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import TractorCard from './components/TractorCard';
import EmptyState from './components/EmptyState';
import Modal from './components/Modal';
import { useLayout } from '../../../hooks/screen/useLayout';
import { ampli } from '../../../ampli';
import { useDeletePokeToUserOwnTractor } from '../../../hooks/api/product/mutations/useDeletePokeToUserOwnTractor.mutation';
import { IMyPokeTractor } from '../../../interfaces/OwnTractor.interfaces';
import {
  useGetMyPokeTractors,
  MY_POKE_TRACTORS_QUERY_KEY,
} from '../../../hooks/api/user/queries/useGetMyPokeTractors.query';
import { usePatchMyPokeTractor } from '../../../hooks/api/user/mutations/usePatchMyPokeTractor.mutation';
import { MainHead } from '../../../components/common/head/MainHead';

interface RemovedTractorInfo {
  tractor: IMyPokeTractor;
  originalIndex: number;
}

function MyInfoPokeListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: tractors } = useGetMyPokeTractors();
  const { mutate: deletePokeToUserOwnTractor } = useDeletePokeToUserOwnTractor();
  const { mutate: patchMyPokeTractor } = usePatchMyPokeTractor();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [removedTractorInfo, setRemovedTractorInfo] = useState<RemovedTractorInfo | null>(null);

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: true }} headerTitle={'찔러본 트랙터 관리'} />,
    fixedHeaderHeight: 48,
    fixedFooter: <></>,
    fixedFooterHeight: 0,
  });

  const handleCancel = async (id: string) => {
    if (!tractors) return;

    const index = tractors && tractors.findIndex((t) => t.id === id);
    const tractor = tractors && tractors[index];

    ampli.pvmClickCancelPokeButton();

    if (tractor) {
      try {
        deletePokeToUserOwnTractor({ tractorId: id });
        setRemovedTractorInfo({ tractor, originalIndex: index });
        setIsModalOpen(true);

        queryClient.setQueryData(MY_POKE_TRACTORS_QUERY_KEY, (oldData: IMyPokeTractor[] | undefined) => {
          if (!oldData) return [];
          return oldData.filter((t) => t.id !== id);
        });
      } catch (error) {
        console.error('Failed to cancel poke:', error);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setRemovedTractorInfo(null);
  };

  const handleUndo = async () => {
    if (!removedTractorInfo) return;

    try {
      patchMyPokeTractor(removedTractorInfo.tractor.id);

      queryClient.setQueryData(MY_POKE_TRACTORS_QUERY_KEY, (oldData: IMyPokeTractor[] | undefined) => {
        if (!oldData) return [removedTractorInfo.tractor];

        const newData = [...oldData];
        newData.splice(removedTractorInfo.originalIndex, 0, removedTractorInfo.tractor);
        return newData;
      });

      setIsModalOpen(false);
      setRemovedTractorInfo(null);
    } catch (error) {
      console.error('Failed to restore poke:', error);
    }
  };

  return (
    <div className={'w-full bg-[#dcdde0]'}>
      <div className={'w-full bg-white'}>
        <div className={'flex-1 flex flex-col min-h-[calc(100vh-40px)] pb-[52px]'}>
          {tractors && tractors.length > 0 ? (
            <div className={'px-4 pb-[32px]'}>
              <div className={'flex flex-col'}>
                {tractors.map((tractor, index) => (
                  <TractorCard
                    key={tractor.id}
                    {...tractor}
                    isLast={index === tractors.length - 1}
                    onCancel={handleCancel}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className={'flex-1 flex items-center justify-center px-4'}>
              <EmptyState message={'찔러보기 한 트랙터가 없어요'} buttonLabel={'더 보러가기'} />
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={handleModalClose} onUndo={handleUndo} />
    </div>
  );
}

export default MyInfoPokeListPage;
